/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { Field } from 'formik'

import type { EuiRadioGroupOption, EuiRadioGroupProps } from '@elastic/eui'
import { EuiFormErrorText, EuiFormRow, EuiRadio } from '@elastic/eui'

import type { FormattedMessage } from '@modules/i18n'

import type { FunctionComponent, ReactElement } from 'react'
import type { StrictOmit } from '../../ts-essentials'

interface Props extends StrictOmit<EuiRadioGroupProps, 'onChange' | 'idSelected'> {
  name: string
  label?: string | ReactElement<typeof FormattedMessage>
  helpText?: string | ReactElement<typeof FormattedMessage> | null

  // Make options required. Also add properties missing from the tsdefs
  options: Array<EuiRadioGroupOption & { disabled?: boolean; value: string }>
}

export const CuiFormRadioGroup: FunctionComponent<Props> = ({
  label,
  helpText,
  options,
  name,
  className,
  disabled,
}) => (
  <EuiFormRow label={label} helpText={helpText}>
    <div className={className}>
      {options.map((option, index) => {
        const { disabled: isOptionDisabled, ...optionRest } = option
        return (
          <Field key={index} name={name}>
            {({ field: { onChange, onBlur, value: currentValue }, form: { touched, errors } }) => {
              // field.name is just a string, the path to a field in the form values.
              // The structure of `touched` mirrors this, whereas `errors` is created
              // by a custom validation function. Our convention is use a flat structure
              // for errors, so that we can simply key on the field path.
              let error = null

              if (index === options.length - 1) {
                error = touched[name] && errors[name]
              }

              return (
                <Fragment>
                  <EuiRadio
                    label={label}
                    name={name}
                    checked={option.value === currentValue}
                    disabled={disabled || isOptionDisabled}
                    onChange={onChange}
                    onBlur={onBlur}
                    {...optionRest}
                  />
                  {error != null && <EuiFormErrorText>{error}</EuiFormErrorText>}
                </Fragment>
              )
            }}
          </Field>
        )
      })}
    </div>
  </EuiFormRow>
)
